import { MoreHorizontal, RefreshCw } from 'lucide-react'
import { Button, ErrorBanner, Menu, PageHeader, Tabs, TabPanel, type MenuItem } from '../components/ui'
import { DarkWebContext } from './darkweb/DarkWebContext'
import { useDarkWebPageState } from './darkweb/useDarkWebPageState'
import { formatRelative, nextScheduledScan } from './darkweb/format'
import type { PageTab } from './darkweb/types'
import { OverviewTab } from './darkweb/tabs/OverviewTab'
import { PeopleTab } from './darkweb/tabs/PeopleTab'
import { BreachesTab } from './darkweb/tabs/BreachesTab'
import { ExternalThreatsTab } from './darkweb/tabs/ExternalThreatsTab'

const TAB_ID = 'darkweb'

// Dark Web Intelligence page, rebuilt on the shared UI primitives (#397 step 3d).
// Tabs fetch their own data; page-level state is shared through DarkWebContext.
export default function DarkWeb() {
  const page = useDarkWebPageState()
  const { user, canRemediate, loading, loadError, loadData, scanning, exporting, dashboard, domains, credentialStats, pageTab, setPageTab, handleScan, handleExport } = page

  if (loading && !dashboard) {
    return (
      <div className="flex h-64 items-center justify-center" role="status">
        <RefreshCw className="h-6 w-6 animate-spin text-muted" aria-hidden="true" />
        <span className="sr-only">Loading dark web data</span>
      </div>
    )
  }

  const lastScan = dashboard?.lastScan
  const partialScanDomains = Object.entries(lastScan?.sourceErrors || {})

  const tabs: Array<{ key: PageTab; label: string; count?: number }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'people', label: 'People', count: credentialStats?.exposed_employees },
    { key: 'customers', label: 'Customers', count: credentialStats?.exposed_customers },
    { key: 'breaches', label: 'Breaches', count: dashboard?.breakdown.uniqueBreaches },
    { key: 'threats', label: 'External threats' },
  ]

  const moreItems: MenuItem[] = [
    ...(canRemediate ? [{ key: 'export', label: exporting ? 'Exporting…' : 'Export CSV (no passwords)', onSelect: handleExport, disabled: exporting }] : []),
    ...(user?.role === 'admin' ? [{ key: 'rescan', label: 'Full rescan…', onSelect: () => handleScan(true), destructive: true, disabled: scanning }] : []),
  ]

  return (
    <DarkWebContext.Provider value={page}>
      <div className="flex flex-col gap-5 text-ink">
        <PageHeader
          breadcrumb="Risk / Dark Web"
          title="Dark Web Intelligence"
          meta={
            <>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-good" aria-hidden="true" />
                Monitoring {domains.length} domain{domains.length === 1 ? '' : 's'}
              </span>
              <span aria-hidden="true">·</span>
              <span>Last scan {formatRelative(lastScan?.finishedAt)}</span>
              <span aria-hidden="true">·</span>
              <span>Next scan {nextScheduledScan()}</span>
            </>
          }
          actions={
            <>
              {canRemediate && (
                <Button variant="primary" icon={<RefreshCw className={`h-4 w-4 ${scanning ? 'animate-spin' : ''}`} aria-hidden="true" />} disabled={scanning} onClick={() => handleScan(false)}>
                  {scanning ? 'Scanning…' : 'Check now'}
                </Button>
              )}
              {moreItems.length > 0 && (
                <Menu
                  items={moreItems}
                  trigger={props => (
                    <Button {...props} aria-label="More actions" className="w-[38px] !px-0">
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                />
              )}
            </>
          }
        />

        {loadError && <ErrorBanner title="Couldn't load dark web data" description={`${loadError} Results may be incomplete; this does not mean no exposures were found.`} onRetry={loadData} />}

        {partialScanDomains.length > 0 && lastScan && (
          <ErrorBanner
            tone="warning"
            title={`The last scan (${formatRelative(lastScan.finishedAt)}) was incomplete`}
            description={`Some sources didn't respond, so these domains may be missing exposures: ${partialScanDomains.map(([domain, errors]) => `${domain} (${Object.keys(errors).join(', ')})`).join('; ')}.`}
          />
        )}

        <Tabs label="Dark Web sections" idPrefix={TAB_ID} tabs={tabs} value={pageTab} onChange={setPageTab} />

        {/* Overview stays mounted so the AI summary isn't regenerated on every tab switch */}
        <TabPanel tabKey="overview" idPrefix={TAB_ID} hidden={pageTab !== 'overview'}>
          <OverviewTab />
        </TabPanel>
        {pageTab === 'people' && (
          <TabPanel tabKey="people" idPrefix={TAB_ID}>
            <PeopleTab category="employee" />
          </TabPanel>
        )}
        {pageTab === 'customers' && (
          <TabPanel tabKey="customers" idPrefix={TAB_ID}>
            <PeopleTab category="customer" />
          </TabPanel>
        )}
        {pageTab === 'breaches' && (
          <TabPanel tabKey="breaches" idPrefix={TAB_ID}>
            <BreachesTab />
          </TabPanel>
        )}
        {pageTab === 'threats' && (
          <TabPanel tabKey="threats" idPrefix={TAB_ID}>
            <ExternalThreatsTab />
          </TabPanel>
        )}
      </div>
    </DarkWebContext.Provider>
  )
}
